import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";

// wrappers for material ui components for formik
import { TextField, Select } from "formik-material-ui";

import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";

import { FormDialogBase } from "../../shared/formhelpers/FormDialogBase";

import { validationSchema } from "./AssetFormMetaData";

import { useQuery, useApolloClient, gql } from "@apollo/client";
import { useAssetAdd, useAssetUpdate } from "../../../api/APIHooks";
import { OperationTargets } from "../../../api/CommonCrudOperations";

import { addMultipleActionItems } from "../../../api/ActionItemApi";

import { getAsset } from "../../../graphql/queries";

import { AssetTypes, NonChoice } from "../../../Enums";

import { useFamilyContext } from "../../../context/FamilyContext";

import { constructCorrespondingActionItems } from "../AssetHelper";

import InsurerSelect from "../../shared/formhelpers/InsurerSelect";

const useStyles = makeStyles((theme) => ({
  flex: {
    flex: 1,
  },
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
}));

const AddEditAssetForm = (props) => {
  const classes = useStyles();
  const client = useApolloClient();
  const familyContext = useFamilyContext();

  const [open, setOpen] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [asset, setAsset] = useState({
    name: "",
    description: "",
    type: NonChoice,
    notes: "",
    insurerId: null,
    insurance: null,
    AssetActionItems: [],
    AssetDocumentItems: [],
  });

  const [skipQuery, setSkipQuery] = useState(true);

  let { loading, error, data } = useQuery(gql(getAsset), {
    variables: { id: props.selected ? props.selected.id : "" },
    skip: skipQuery,
  });

  const [addOneAsset] = useAssetAdd(familyContext.familyRealm.id, (data) => {
    // console.log(data);
    // if (props.handleReceiveData) {
    //   props.handleReceiveData(data);
    // }
  });

  const [updateOneAsset] = useAssetUpdate(familyContext.familyRealm.id, (data) => {
      // console.log(data);
      // if (props.handleReceiveData) {
      //   props.handleReceiveData(data);
      // }
    }
  );

  useEffect(() => {
    if (!skipQuery) {
      const onCompleted = (data) => {
        setAsset(data.item);
      };
      const onError = (error) => {
        return <div>{error}</div>;
      };

      if (onCompleted || onError) {
        if (onCompleted && !loading) {
          onCompleted(data);
          setSkipQuery(true);
        } else if (onError && !loading && error) {
          onError(error);
          setSkipQuery(true);
        }
      }
    }
  }, [loading, data, error]);

  useEffect(() => {
    const { selected } = props;

    // if we're editing and we haven't yet fetched the item
    if (selected && skipQuery) {
      setSkipQuery(false);
    }

    return () => {
      setSkipQuery(true);
    };
  }, [props.selected]);

  const save = (values) => {
    // alert(JSON.stringify(values, null, 2));

    // Mutation - add one
    const details = {
      userRealm: familyContext.familyRealm.id,
      name: values.name,
      type: values.type ? values.type : "",
      description: values.description ? values.description : "",
      notes: values.notes ? values.notes : "",
    };

    // if we have an insurer, add it here
    if (values.insurance) {
      details.insurerId = values.insurance.id
    }

    // props.open means we're here for editing - otherwise we're adding
    props.open ? updateOne(values, details) : addOne(values, details);
  };

  const addOne = (values, details) => {
    const actionItems = constructCorrespondingActionItems(values, details);
    if (actionItems.length > 0) {
      details.AssetActionItems = {
        data: [...actionItems],
      };
    }

    addOneAsset({ variables: { object: details } })
      .then((result) => {
        props.handleAdd(result.data.item);
      })
      .catch((error) => {
        console.error(error);
      });

    // hide the modal
    handleClose();
  };

  const updateOne = (values, details) => {
    if (asset.actionItems.length === 0) {
      const actionItems = constructCorrespondingActionItems(values, details);
      if (actionItems.length > 0) {
        const items = actionItems.map((item) => {
          item.userRealm = familyContext.familyRealm.id;
          item.domainId = asset.id;
          return item;
        });

        addMultipleActionItems(
          client,
          familyContext.familyRealm.id,
          OperationTargets.Assets,
          items
        )
          .then((result) => {
            console.log(result);
            console.log("Added %s action items", actionItems.length);
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            // hide the modal
            handleClose();
          });
      }
    }

    // finally, update the asset
    updateOneAsset({ variables: { id: asset.id, changes: details } })
      .then((result) => {
        props.handleEdit(result.data.item);
      })
      .catch((error) => {
        console.error(error);
      });

    // hide the modal
    handleClose();
  };

  // Attach this to your <Formik>
  const formRef = useRef();

  const handleSubmitClick = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const handleResetClick = () => {
    if (formRef.current) {
      formRef.current.handleReset();
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  // normal close and cancel
  const handleClose = () => {
    // if we were opened by the client, let them close us, otherwise we close
    props.handleClose ? props.handleClose() : setOpen(false);
  };

  const body = (
    <Formik
      innerRef={formRef}
      enableReinitialize={true}
      initialValues={asset}
      validationSchema={validationSchema()}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false);

        // actually save the form
        save(values);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        handleReset,
        setFieldValue,
        isSubmitting,
        isValid,
        /* and other goodies */
      }) => (
        <Form onReset={handleReset} onSubmit={handleSubmit}>
          <FormControl className={classes.formControl}>
            <Field
              component={TextField}
              name="name"
              type="text"
              label="Name"
              autoFocus
              helperText={
                touched.name ? errors.name : "Provide a name for this asset"
              }
              error={touched.name && Boolean(errors.name)}
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel htmlFor="assettype-type">Asset Type</InputLabel>
            <Field
              component={Select}
              name="type"
              inputProps={{
                id: "assettype-type",
              }}
            >
              {AssetTypes.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Field>
            <FormHelperText>What type of asset is this?</FormHelperText>
          </FormControl>
          <FormControl className={classes.formControl}>
            <Field
              component={TextField}
              name="description"
              type="text"
              label="Description"
              helperText="Describe the asset (i.e. 1992 red Toyota)"
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <InsurerSelect
              existingInsurer={asset.insurerId === null ? undefined : asset.insurance} 
              onChange={(insurance) => {
                setFieldValue('insurance', insurance ? insurance: null);
              }}
            />  
            <FormHelperText>If insured, identify the insurance company</FormHelperText>
          </FormControl>
          <FormControl className={classes.formControl}>
            <Field
              component={TextField}
              name="notes"
              type="text"
              label="Notes"
              multiline
              maxRows="3"
              helperText="Add any additional notes about this asset (i.e. 'DO not sell this, keep it in the family')"
            />
          </FormControl>
        </Form>
      )}
    </Formik>
  );

  const title = props.open ? "Edit an asset" : "Add an asset";

  return (
    <FormDialogBase
      open={props.open ? props.open : open}
      title={title}
      body={body}
      submitEnabled={submitEnabled}
      handleAddClickOpen={handleClickOpen}
      handleClose={handleClose}
      handleReset={handleResetClick}
      handleSubmit={handleSubmitClick}
    />
  );
};

// client can open if they choose to OR we will open
AddEditAssetForm.defaultProps = {
  selected: null,
  open: false,
  handleClose: null,
};

AddEditAssetForm.propTypes = {
  selected: PropTypes.object,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleAdd: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleReceiveData: PropTypes.func.isRequired,
};

export default AddEditAssetForm;
